import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../../../styles/panel/user/ManageOrders.css";
import http from "../../../api/http";
import toast, { Toaster } from "react-hot-toast";
import {
  ORDER_STATUS_MAP,
  PRODUCT_STATUS_KEYS,
  PRODUCT_STATUS_MAP,
} from "./ManageOrders";
import { swalConfirm } from "../../../utils/swalAlert";

// کلید وضعیت «تایید شده» برای کالا (در صورت متفاوت بودن با ManageOrders اصلاح شود)
const ITEM_APPROVED = "approved";

// تبدیل ارقام فارسی/عربی به انگلیسی و حذف هر کاراکتر غیرعددی
const toEnglishDigits = (value) =>
  String(value ?? "")
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
    .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d))
    .replace(/\D/g, "");

/* ============ صفحه‌ی جزئیات سفارش ============ */
export default function OrderDetails() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingItemId, setSavingItemId] = useState(null);
  const [shippingCost, setShippingCost] = useState("");
  // مبلغی که کاربر تایید کرده است (با تغییر مبلغ، تایید باطل می‌شود)
  const [confirmedAmount, setConfirmedAmount] = useState(null);
  // مبلغ قابل پرداختِ دریافت‌شده از سرور (وقتی از قبل کالای تایید شده وجود دارد)
  // با هر تغییر در وضعیت کالاها یا هزینه ارسال null می‌شود و محاسبه‌ی محلی جایگزین می‌شود
  const [serverTotal, setServerTotal] = useState(null);
  // در حال ارسال درخواست تایید مبلغ
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    http
      .get(`/orders/user/orders/${orderId}/`)
      .then((res) => {
        const results = res.data || res.data?.results || null;
        setOrder(results);

        const shipping = results?.shipping_price ?? results?.shipping_cost;
        setShippingCost(shipping ? String(Math.round(Number(shipping))) : "");

        // اگر از قبل کالای تایید شده وجود دارد، مبلغ قابل پرداخت از سرور گرفته می‌شود
        const hasApprovedOnLoad = (results?.items || []).some(
          (it) => it.status === ITEM_APPROVED,
        );
        const savedTotal = results?.total_price;
        if (hasApprovedOnLoad && savedTotal != null && savedTotal !== "") {
          const total = Number(savedTotal);
          setServerTotal(total);
          setConfirmedAmount(total);
        }
      })
      .catch((err) => {
        console.error("order error:", err);
        toast.error(
          err?.response?.data?.detail ||
            "خطایی در دریافت اطلاعات سفارش رخ داد.",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [orderId]);

  const handleItemStatusChange = (itemId, newStatus) => {
    const prevStatus = order.items.find((it) => it.id === itemId)?.status;
    if (prevStatus === newStatus) return;

    // از این لحظه مبلغ قابل پرداخت دوباره به‌صورت محلی محاسبه می‌شود
    setServerTotal(null);

    setOrder((prev) => ({
      ...prev,
      items: prev.items.map((it) =>
        it.id === itemId ? { ...it, status: newStatus } : it,
      ),
    }));
    setSavingItemId(itemId);

    http
      .patch(`orders/update-item-status/${itemId}/`, {
        status: newStatus,
      })
      .then(() => {
        toast.success("وضعیت با موفقیت تغییر کرد");
      })
      .catch((err) => {
        console.error("item status update error:", err);

        setOrder((prev) => ({
          ...prev,
          items: prev.items.map((it) =>
            it.id === itemId ? { ...it, status: prevStatus } : it,
          ),
        }));

        toast.error(
          err?.response?.data?.detail || "خطایی در انجام عملیات رخ داد.",
        );
      })
      .finally(() => setSavingItemId(null));
  };

  // -----------------------------
  // Payable calculation
  // -----------------------------
  const items = order?.items || [];
  // فقط کالاهای تایید شده در جمع پرداختی محاسبه می‌شوند
  const approvedItems = items.filter((it) => it.status === ITEM_APPROVED);
  const hasApproved = approvedItems.length > 0;
  const itemsTotal = approvedItems.reduce(
    (sum, it) => sum + Number(it.price) * Number(it.quantity),
    0,
  );
  const shippingAmount = Number(shippingCost) || 0;
  // هزینه‌ی ارسال از لحظه‌ی تایید اولین کالا به جمع اضافه می‌شود
  const appliedShipping = hasApproved ? shippingAmount : 0;
  const payableTotal =
    serverTotal !== null ? serverTotal : itemsTotal + appliedShipping;

  const isAmountConfirmed = hasApproved && confirmedAmount === payableTotal;
  const needsAmountConfirm = hasApproved && !isAmountConfirmed;

  // تایید مبلغ: ارسال هزینه ارسال و مبلغ کل به سرور؛ در صورت موفقیت true برمی‌گرداند
  const handleConfirmAmount = async () => {
    if (!hasApproved || confirming) return false;

    const amountToConfirm = payableTotal;
    setConfirming(true);

    try {
      await http.patch(`orders/${orderId}/price/`, {
        shipping_price: appliedShipping,
        total_price: amountToConfirm,
      });
      setConfirmedAmount(amountToConfirm);
      toast.success("مبلغ قابل پرداخت تایید شد");
      return true;
    } catch (err) {
      console.error("confirm price error:", err);
      toast.error(err?.response?.data?.detail || "خطایی در تایید مبلغ رخ داد.");
      return false;
    } finally {
      setConfirming(false);
    }
  };

  const handleLeave = async () => {
    const confirmed = await swalConfirm({
      title: "مبلغ قابل پرداخت تایید نشده است",
      text: `لطفاً مبلغ "${payableTotal.toLocaleString("fa-IR")}" تومان را تایید کنید.`,
      confirmText: "تایید مبلغ",
      cancelText: "خروج بدون تایید",
    });

    if (confirmed) {
      // اول مبلغ تایید و ذخیره می‌شود، بعد خروج
      const ok = await handleConfirmAmount();
      if (ok) leavePage();
    } else {
      leavePage();
    }
  };
  const leavePage = () => navigate("/panel/user/manage-orders");

  const onBack = () => {
    if (!needsAmountConfirm) {
      leavePage();
      return;
    }

    handleLeave();
  };

  // هشدار مرورگر هنگام بستن تب یا رفرش، تا وقتی مبلغ تایید نشده
  useEffect(() => {
    if (!needsAmountConfirm) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [needsAmountConfirm]);

  // -----------------------------
  // Loading
  // -----------------------------
  if (loading) {
    return (
      <div className="order-details-page">
        <div className="details-loading">در حال دریافت اطلاعات سفارش...</div>
      </div>
    );
  }

  // -----------------------------
  // No order
  // -----------------------------
  if (!order) {
    return (
      <div className="order-details-page">
        <button className="details-back-btn" onClick={onBack}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 6l6 6-6 6" />
          </svg>
          بازگشت به لیست سفارش‌ها
        </button>

        <div className="details-card">سفارشی پیدا نشد.</div>
      </div>
    );
  }

  // -----------------------------
  // Status
  // -----------------------------
  const currentStatus = ORDER_STATUS_MAP[order.status] || {
    cls: "",
    label: "تعیین نشده",
  };

  return (
    <div className="order-details-page">
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            fontFamily: "inherit",
            direction: "rtl",
            fontSize: "13.5px",
          },
        }}
      />

      {/* =========================
          Back
      ========================= */}
      <button className="details-back-btn" onClick={onBack}>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9 6l6 6-6 6" />
        </svg>
        بازگشت به لیست سفارش‌ها
      </button>

      {/* =========================
          Order Header
      ========================= */}
      <div className="details-order-head">
        <div>
          <h1>جزئیات سفارش {order.order_code || "-"}</h1>
        </div>

        <span className={`status-badge ${currentStatus.cls}`}>
          {currentStatus.label}
        </span>
      </div>

      {/* =========================
          Customer Info
      ========================= */}
      <div className="details-card">
        <h2 className="details-section-title">اطلاعات مشتری</h2>

        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">نام</span>
            <span className="info-value">{order.customer?.name || "-"}</span>
          </div>

          <div className="info-item">
            <span className="info-label">موبایل</span>
            <span className="info-value" dir="ltr">
              {order.customer?.phone || "-"}
            </span>
          </div>

          <div className="info-item">
            <span className="info-label">استان</span>
            <span className="info-value">
              {order.customer?.province || "-"}
            </span>
          </div>

          <div className="info-item">
            <span className="info-label">شهر</span>
            <span className="info-value">{order.customer?.city || "-"}</span>
          </div>

          <div className="info-item info-item-full">
            <span className="info-label">آدرس</span>
            <span className="info-value">{order.customer?.address || "-"}</span>
          </div>

          <div className="info-item">
            <span className="info-label">کد پستی</span>
            <span className="info-value" dir="ltr">
              {order.customer?.postal_code || "-"}
            </span>
          </div>
        </div>
      </div>

      {/* =========================
          Products
      ========================= */}
      <div className="details-card">
        <h2 className="details-section-title">محصولات سفارش</h2>

        <div className="products-table-scroll">
          <table className="products-table">
            <thead>
              <tr>
                <th style={{ width: "80px", textAlign: "center" }}>تصویر</th>
                <th>نام محصول</th>
                <th>رنگ</th>
                <th>تعداد</th>
                <th>قیمت واحد</th>
                <th>وضعیت</th>
              </tr>
            </thead>

            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td data-label="تصویر" style={{ textAlign: "center" }}>
                    {it.image ? (
                      <img
                        src={it.image}
                        alt={it.product_name}
                        style={{
                          width: "50px",
                          height: "50px",
                          borderRadius: "8px",
                          objectFit: "cover",
                          border: "1px solid #e2e8f0",
                          verticalAlign: "middle",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          borderRadius: "8px",
                          backgroundColor: "#f1f5f9",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "bold",
                          color: "#94a3b8",
                          fontSize: "16px",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        {(it.product_name || "؟").charAt(0)}
                      </div>
                    )}
                  </td>
                  <td
                    data-label="نام محصول"
                    style={{ fontWeight: "600", color: "#1e293b" }}
                  >
                    {it.product_name}
                  </td>
                  <td data-label="رنگ">{it.color_name || "-"}</td>
                  <td data-label="تعداد">
                    {it.quantity?.toLocaleString("fa-IR")}
                  </td>
                  <td data-label="قیمت واحد">
                    {Number(it.price).toLocaleString("fa-IR")} تومان
                  </td>
                  <td data-label="وضعیت" className="cell-item-status">
                    <select
                      className="status-select item-status-select"
                      value={it.status ?? ""}
                      disabled={savingItemId === it.id}
                      onChange={(e) =>
                        handleItemStatusChange(it.id, e.target.value)
                      }
                    >
                      <option value="" disabled>
                        تعیین نشده
                      </option>
                      {PRODUCT_STATUS_KEYS.map((k) => (
                        <option key={k} value={k}>
                          {PRODUCT_STATUS_MAP[k].label}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---- جمع پرداختی ---- */}
      <div className="details-card payable-card">
        <p className="payable-note">
          با تایید هر کالا، مبلغ آن به جمع پرداختی اضافه می‌شود.
        </p>
        <p className="payable-note">
          هزینه ارسال از زمان تایید اولین کالا به مبلغ قابل پرداخت اضافه می‌شود.
        </p>

        <div className="shipping-row">
          <label className="shipping-label" htmlFor="shipping-cost">
            هزینه ارسال
          </label>
          <div className="shipping-input-wrap">
            <input
              id="shipping-cost"
              className="shipping-input"
              type="text"
              inputMode="numeric"
              dir="ltr"
              placeholder="۰"
              autoComplete="off"
              value={
                shippingCost ? Number(shippingCost).toLocaleString("fa-IR") : ""
              }
              onChange={(e) => {
                setServerTotal(null);
                setShippingCost(toEnglishDigits(e.target.value));
              }}
            />
            <span className="shipping-unit">تومان</span>
          </div>
        </div>

        {hasApproved && serverTotal === null && (
          <>
            <div className="payable-line">
              <span>جمع کالاها</span>
              <span>{itemsTotal.toLocaleString("fa-IR")} تومان</span>
            </div>
            <div className="payable-line">
              <span>هزینه ارسال</span>
              <span>{appliedShipping.toLocaleString("fa-IR")} تومان</span>
            </div>
          </>
        )}

        <div className="payable-total-row">
          <span>مبلغ قابل پرداخت</span>
          <div className="payable-amount-wrap">
            <span className={`payable-amount${hasApproved ? " ready" : ""}`}>
              {payableTotal.toLocaleString("fa-IR")} تومان
            </span>
            <button
              type="button"
              className={`payable-confirm-btn${
                isAmountConfirmed
                  ? " confirmed"
                  : needsAmountConfirm
                    ? " attention"
                    : ""
              }`}
              onClick={handleConfirmAmount}
              disabled={!hasApproved || isAmountConfirmed || confirming}
              aria-label={isAmountConfirmed ? "مبلغ تایید شده" : "تایید مبلغ"}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12.5l4.5 4.5L19 7.5" />
              </svg>
              <span>{isAmountConfirmed ? "مبلغ تایید شد" : "تایید مبلغ"}</span>
            </button>
          </div>
        </div>

        {needsAmountConfirm && (
          <p className="payable-confirm-hint">
            مبلغ قابل پرداخت هنوز تایید نشده است. لطفاً روی «تایید مبلغ» بزنید.
          </p>
        )}
      </div>
    </div>
  );
}
